/**
 * Entrega Expressa — disponibilidade por SKU (FullComm inventory/availability).
 * Lê CEP/locations de localStorage, consulta estoque e mostra/oculta selos [data-express-sku].
 *
 * Performance: pinta imediatamente a partir do cache; só chama a API para SKUs ainda desconhecidos.
 */
(function () {
  if (window.__haomaExpressAvailabilityInit) return;
  window.__haomaExpressAvailabilityInit = true;

  var CEP_STORAGE_KEY = 'haoma-cep-express';
  var AVAIL_CACHE_KEY = 'haoma-express-availability';
  var CACHE_TTL_MS = 30 * 60 * 1000;
  var BATCH_SIZE = 100;
  var REQUEST_TIMEOUT_MS = 10000;
  var DEBOUNCE_MS = 80;

  var config = window.haomaExpressConfig || {};
  var shop = config.shop || '';
  var apiBase = (config.apiBase || 'https://oms.fullcomm.app').replace(/\/$/, '');
  var running = false;
  var pending = false;
  var debounceTimer = null;
  var lastCepChecked = '';
  var memoryMap = null;
  var memoryCep = '';

  function getCepData() {
    try {
      var raw = localStorage.getItem(CEP_STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.cep) return null;
      if (!Array.isArray(data.locations)) data.locations = [];

      var ttlDays = typeof data.ttl_days === 'number' ? data.ttl_days : 30;
      if (data.resolved_at) {
        var resolved = Date.parse(data.resolved_at);
        if (!isNaN(resolved) && Date.now() - resolved > ttlDays * 24 * 60 * 60 * 1000) {
          return null;
        }
      }

      return data;
    } catch (e) {
      return null;
    }
  }

  function getLocationIds(cepData) {
    return (cepData.locations || [])
      .map(function (loc) { return String(loc.locationId || ''); })
      .filter(Boolean);
  }

  function isUncoveredCep(cepData) {
    if (!cepData) return false;
    return cepData.covered === false || !getLocationIds(cepData).length;
  }

  function applyStandardToAll(elements) {
    elements.forEach(function (el) {
      var skus = parseSkus(el.getAttribute('data-express-sku'));
      if (!skus.length) return;

      var badges = getDeliveryBadges(el);
      hideBadge(badges.express);
      if (badges.standard) {
        showBadge(badges.standard);
      }
    });
  }

  function collectSkuElements() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-express-sku]'));
  }

  function parseSkus(value) {
    return String(value || '')
      .split(',')
      .map(function (sku) { return sku.trim(); })
      .filter(Boolean);
  }

  function uniqueSkus(elements) {
    var set = {};
    elements.forEach(function (el) {
      parseSkus(el.getAttribute('data-express-sku')).forEach(function (sku) {
        set[sku] = true;
      });
    });
    return Object.keys(set);
  }

  function readCache(cep) {
    if (memoryMap && memoryCep === cep) {
      return memoryMap;
    }

    try {
      var raw = sessionStorage.getItem(AVAIL_CACHE_KEY);
      if (!raw) {
        raw = localStorage.getItem(AVAIL_CACHE_KEY);
      }
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (!cached || cached.cep !== cep || !cached.map) return null;
      if (!cached.expiresAt || Date.now() > cached.expiresAt) return null;
      memoryMap = cached.map;
      memoryCep = cep;
      return cached.map;
    } catch (e) {
      return null;
    }
  }

  function writeCache(cep, map) {
    memoryMap = map;
    memoryCep = cep;
    var payload = JSON.stringify({
      cep: cep,
      map: map,
      expiresAt: Date.now() + CACHE_TTL_MS
    });
    try {
      sessionStorage.setItem(AVAIL_CACHE_KEY, payload);
    } catch (e) {
      /* ignore */
    }
    try {
      localStorage.setItem(AVAIL_CACHE_KEY, payload);
    } catch (e) {
      /* ignore */
    }
  }

  function clearAvailabilityCache() {
    memoryMap = null;
    memoryCep = '';
    try {
      sessionStorage.removeItem(AVAIL_CACHE_KEY);
    } catch (e) {
      /* ignore */
    }
    try {
      localStorage.removeItem(AVAIL_CACHE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function chunk(arr, size) {
    var out = [];
    for (var i = 0; i < arr.length; i += size) {
      out.push(arr.slice(i, i + size));
    }
    return out;
  }

  function fetchWithTimeout(url, options, timeoutMs) {
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, timeoutMs);
    var opts = Object.assign({}, options || {}, { signal: controller.signal });
    return fetch(url, opts).finally(function () {
      clearTimeout(timeoutId);
    });
  }

  function buildAvailabilityMap(items) {
    var map = {};
    (items || []).forEach(function (item) {
      if (!item || !item.sku) return;
      var available = (item.locations || []).some(function (loc) {
        return Number(loc.available) > 0;
      });
      map[String(item.sku)] = !!available;
    });
    return map;
  }

  async function fetchAvailability(skus, locationIds) {
    var map = {};
    var batches = chunk(skus, BATCH_SIZE);
    var url = apiBase + '/inventory/availability/' + encodeURIComponent(shop);

    // Um lote que falha não pode descartar os demais: os SKUs que voltaram
    // ficam conhecidos e só os que faltaram são consultados de novo depois.
    var results = await Promise.all(batches.map(function (batch) {
      return fetchWithTimeout(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          skus: batch,
          locationIds: locationIds
        })
      }, REQUEST_TIMEOUT_MS).then(function (response) {
        if (!response.ok) throw new Error('availability-api');
        return response.json();
      }).catch(function () {
        return null;
      });
    }));

    var failed = 0;
    results.forEach(function (payload) {
      if (!payload) {
        failed += 1;
        return;
      }
      Object.assign(map, buildAvailabilityMap(payload.items));
    });

    if (failed === batches.length) throw new Error('availability-api');

    return map;
  }

  function getDeliveryBadges(wrapper) {
    return {
      express: wrapper.querySelector('[data-delivery-type="express"]'),
      standard: wrapper.querySelector('[data-delivery-type="standard"]')
    };
  }

  function isCartBadgeWrapper(wrapper) {
    if (!wrapper || !wrapper.classList) return false;
    if (wrapper.classList.contains('haoma-delivery-badges--cart')) return true;
    return !!wrapper.closest(
      '#x-cart-drawer-container, #cart-drawer, .cart-drawer, .mini-cart, .main-cart-items, .cart-item, form[action*="/cart"]'
    );
  }

  function isForcedStandardForElement(wrapper) {
    if (window.haomaForceStandardDelivery) return true;
    if (window.haomaForceStandardDeliveryCart) {
      return isCartBadgeWrapper(wrapper);
    }
    return false;
  }

  function showBadge(badge) {
    if (!badge) return;
    badge.hidden = false;
    badge.removeAttribute('hidden');
    badge.classList.add('is-available');
  }

  function hideBadge(badge) {
    if (!badge) return;
    badge.hidden = true;
    badge.setAttribute('hidden', '');
    badge.classList.remove('is-available');
  }

  function applyBadges(elements, map, onlyKnown) {
    elements.forEach(function (el) {
      var skus = parseSkus(el.getAttribute('data-express-sku'));
      var badges = getDeliveryBadges(el);
      var expressBadge = badges.express;
      var standardBadge = badges.standard;

      if (!expressBadge && !standardBadge) {
        expressBadge = el;
        standardBadge = null;
      }

      if (!skus.length) {
        hideBadge(expressBadge);
        hideBadge(standardBadge);
        return;
      }

      // Cliente aceitou manter brinde padrão: todo o pedido vira entrega padrão
      // (reflete em carrinho, cards e PDP).
      if (isForcedStandardForElement(el) && standardBadge) {
        hideBadge(expressBadge);
        showBadge(standardBadge);
        return;
      }

      var known = skus.filter(function (sku) { return typeof map[sku] !== 'undefined'; });
      if (onlyKnown && !known.length) return;

      var isAvailable = known.some(function (sku) { return !!map[sku]; });

      if (onlyKnown && !isAvailable && known.length < skus.length) return;

      if (isAvailable) {
        showBadge(expressBadge);
        hideBadge(standardBadge);
      } else if (!onlyKnown || known.length === skus.length) {
        hideBadge(expressBadge);
        showBadge(standardBadge);
      }
    });
  }

  function hideAllBadges(elements) {
    elements.forEach(function (el) {
      var badges = getDeliveryBadges(el);
      if (badges.express || badges.standard) {
        hideBadge(badges.express);
        hideBadge(badges.standard);
      } else {
        hideBadge(el);
      }
    });
  }

  function paintFromCacheNow() {
    if (!shop) return false;
    var cepData = getCepData();
    if (!cepData) return false;

    var elements = collectSkuElements();
    if (!elements.length) return false;

    if (isUncoveredCep(cepData)) {
      applyStandardToAll(elements);
      return true;
    }

    var map = readCache(cepData.cep);
    if (!map) return false;

    applyBadges(elements, map, true);
    return true;
  }

  async function refresh() {
    if (running) {
      pending = true;
      return;
    }

    running = true;
    pending = false;

    try {
      var elements = collectSkuElements();
      if (!shop || !elements.length) {
        hideAllBadges(elements);
        return;
      }

      var cepData = getCepData();
      if (!cepData) {
        hideAllBadges(elements);
        return;
      }

      if (isUncoveredCep(cepData)) {
        if (cepData.cep !== lastCepChecked) {
          if (lastCepChecked) clearAvailabilityCache();
          lastCepChecked = cepData.cep;
        }
        applyStandardToAll(elements);
        return;
      }

      var locationIds = getLocationIds(cepData);
      if (!locationIds.length) {
        applyStandardToAll(elements);
        return;
      }

      var skus = uniqueSkus(elements);
      if (!skus.length) {
        hideAllBadges(elements);
        return;
      }

      if (cepData.cep !== lastCepChecked) {
        if (lastCepChecked) clearAvailabilityCache();
        lastCepChecked = cepData.cep;
      }

      var map = readCache(cepData.cep) || {};

      // Pinta o que já sabemos imediatamente
      applyBadges(elements, map, true);

      var missing = skus.filter(function (sku) { return typeof map[sku] === 'undefined'; });
      if (missing.length) {
        var extra = await fetchAvailability(missing, locationIds);
        Object.assign(map, extra);
        writeCache(cepData.cep, map);
        applyBadges(collectSkuElements(), map, false);
      } else {
        writeCache(cepData.cep, map);
        applyBadges(elements, map, false);
      }
    } catch (e) {
      // Mantém o que já estava pintado pelo cache; só esconde se não houver cache
      if (!memoryMap) hideAllBadges(collectSkuElements());
    } finally {
      running = false;
      if (pending) {
        scheduleRefresh();
      }
    }
  }

  function normalizeSkuList(skus) {
    if (!skus) return [];
    if (Array.isArray(skus)) {
      return skus.map(function (sku) { return String(sku || '').trim(); }).filter(Boolean);
    }
    return String(skus)
      .split(',')
      .map(function (sku) { return sku.trim(); })
      .filter(Boolean);
  }

  async function checkSkus(skus) {
    var list = normalizeSkuList(skus);
    if (!list.length || !shop) return {};

    var cepData = getCepData();
    if (!cepData) return {};

    if (isUncoveredCep(cepData)) {
      var uncovered = {};
      list.forEach(function (sku) {
        uncovered[sku] = false;
      });
      return uncovered;
    }

    var locationIds = getLocationIds(cepData);
    if (!locationIds.length) {
      var noLocations = {};
      list.forEach(function (sku) {
        noLocations[sku] = false;
      });
      return noLocations;
    }

    if (cepData.cep !== lastCepChecked) {
      if (lastCepChecked) clearAvailabilityCache();
      lastCepChecked = cepData.cep;
    }

    var map = readCache(cepData.cep) || {};
    var missing = list.filter(function (sku) { return typeof map[sku] === 'undefined'; });

    if (missing.length) {
      try {
        var extra = await fetchAvailability(missing, locationIds);
        Object.assign(map, extra);
        writeCache(cepData.cep, map);
      } catch (e) {
        /* return partial map from cache */
      }
    }

    var result = {};
    list.forEach(function (sku) {
      if (typeof map[sku] !== 'undefined') {
        result[sku] = !!map[sku];
      }
    });
    return result;
  }

  function scheduleRefresh() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      refresh();
    }, DEBOUNCE_MS);
  }

  function onCepUpdated() {
    clearAvailabilityCache();
    lastCepChecked = '';
    paintFromCacheNow();
    scheduleRefresh();
  }

  function nodeHasExpressSku(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.hasAttribute && node.hasAttribute('data-express-sku')) return true;
    if (node.querySelector && node.querySelector('[data-express-sku]')) return true;
    return false;
  }

  function initObserver() {
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-express-sku') {
          paintFromCacheNow();
          scheduleRefresh();
          return;
        }
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          for (var j = 0; j < mutation.addedNodes.length; j++) {
            if (nodeHasExpressSku(mutation.addedNodes[j])) {
              paintFromCacheNow();
              scheduleRefresh();
              return;
            }
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-express-sku']
    });
  }

  function onVariantChange(event) {
    var variant = event && event.detail && event.detail.variant;
    if (!variant) return;

    var sku = variant.sku || '';
    var pdpBadges = document.querySelectorAll('[data-express-pdp="true"]');
    if (!pdpBadges.length) return;

    pdpBadges.forEach(function (el) {
      el.setAttribute('data-express-sku', sku);
      var badges = getDeliveryBadges(el);
      if (!sku) {
        hideBadge(badges.express);
        hideBadge(badges.standard);
      }
    });

    paintFromCacheNow();
    scheduleRefresh();
  }

  function boot() {
    window.addEventListener('haoma:cep-updated', onCepUpdated);
    document.addEventListener('eurus:cart:items-changed', scheduleRefresh);
    window.addEventListener('eurus:cart:items-changed', scheduleRefresh);
    document.addEventListener('eurus:variant:change', onVariantChange);
    initObserver();

    // Pinta na hora a partir do cache (sem esperar debounce/API)
    paintFromCacheNow();
    // Completa SKUs novos em seguida
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function setForceStandard(val, scope) {
    var active = !!val;
    var cartOnly = scope === 'cart';

    if (cartOnly) {
      window.haomaForceStandardDeliveryCart = active;
    } else {
      window.haomaForceStandardDelivery = active;
      // Ligar ou desligar o force global também zera o do carrinho, para as
      // badges não ficarem presas em "padrão" depois da limpeza.
      window.haomaForceStandardDeliveryCart = false;
    }

    var elements = collectSkuElements();
    if (window.haomaForceStandardDelivery || window.haomaForceStandardDeliveryCart) {
      elements.forEach(function (el) {
        if (!parseSkus(el.getAttribute('data-express-sku')).length) return;
        if (!isForcedStandardForElement(el)) return;
        var badges = getDeliveryBadges(el);
        if (!badges.standard) return;
        hideBadge(badges.express);
        showBadge(badges.standard);
      });
    } else {
      paintFromCacheNow();
      scheduleRefresh();
    }
  }

  window.haomaExpressAvailability = {
    refresh: scheduleRefresh,
    paint: paintFromCacheNow,
    check: checkSkus,
    setForceStandard: setForceStandard
  };
})();
